<app-page-header
  [title]="i18n.t('cmig.title')"
  [subtitle]="i18n.t('cmig.subtitle')"
  [icons]="headerIcons">
</app-page-header>

<div class="custom-model-container">
  <!-- Sidebar -->
  <aside class="sidebar">
    <div class="sidebar-section">
      <h3 class="sidebar-title">{{ i18n.t('cmig.customModel') }}</h3>
      <app-loading *ngIf="isLoadingCustomModels" [message]="i18n.t('cmig.loadingModels')"></app-loading>
      <div class="setting-group" *ngIf="!isLoadingCustomModels">
        <label class="setting-label" for="custom-model-select">{{ i18n.t('cmig.selectModel') }}</label>
        <select
          id="custom-model-select"
          class="setting-select"
          [(ngModel)]="selectedModelId"
          (ngModelChange)="onModelSelect($event)"
          [disabled]="customModels.length === 0">
          <option [ngValue]="null" disabled hidden>{{ i18n.t('cmig.selectModelPlaceholder') }}</option>
          <option *ngFor="let model of customModels" [ngValue]="model.assetId">
            {{ getModelDisplayName(model) }}
          </option>
        </select>
        <p class="empty-models-message" *ngIf="customModels.length === 0">
          {{ i18n.t('cmig.noModels') }}
        </p>
      </div>

      <div class="setting-group hidden">
        <label class="setting-label">{{ i18n.t('cmig.numImages') }}</label>
        <select class="setting-select" [(ngModel)]="generationSettings.numImages">
          <option [value]="1">{{ i18n.t('cmig.numImagesOption1') }}</option>
          <option [value]="2">{{ i18n.t('cmig.numImagesOption2') }}</option>
          <option [value]="4">{{ i18n.t('cmig.numImagesOption4') }}</option>
        </select>
      </div>

      <div class="setting-group ratio-setting-group" *ngIf="!hasStructureImage">
        <label class="setting-label">{{ i18n.t('cmig.ratio') }}</label>
        <select class="setting-select" [(ngModel)]="generationSettings.size">
          <option *ngFor="let opt of sizeOptions" [value]="opt.value">{{ opt.label }}</option>
        </select>
      </div>

      <div class="setting-group">
        <label class="setting-label" for="structureImageInput">{{ i18n.t('cmig.structureImage') }}</label>
        <p class="structure-upload-warning" *ngIf="usesAspectRatioApi">{{ i18n.t('cmig.stuctureImageCm5warning') }}</p>
        <div class="structure-upload">
          <input
            type="file"
            id="structureImageInput"
            class="structure-file-input"
            accept="image/jpeg,image/png,image/gif,image/webp"
            (change)="onStructureFileSelected($event)">
            <div
              class="structure-dropzone"
              *ngIf="!structureImagePreview"
              [class.dragging]="isDraggingStructure"
              (dragover)="onStructureDragOver($event)"
              (dragleave)="onStructureDragLeave($event)"
              (drop)="onStructureDrop($event)">
              <div class="structure-upload-icon" aria-hidden="true">
                <i class="fas fa-cloud-upload-alt"></i>
              </div>
              <p class="structure-upload-text">{{ i18n.t('cmig.dragDrop') }}</p>
              <button
                type="button"
                class="structure-select-btn"
                (click)="triggerStructureFileInput()"
                [disabled]="isUploadingStructure">
                {{ i18n.t('configPanel.selectFile') }}
              </button>
              <p class="structure-upload-formats">{{ i18n.t('cmig.formatsHint') }}</p>
            </div>
            <div class="structure-upload-preview" *ngIf="structureImagePreview">
              <img [src]="structureImagePreview" [alt]="i18n.t('cmig.structureImagePreviewAlt')">
              <button
                type="button"
                class="structure-remove-btn"
                (click)="removeStructureImage()"
                [disabled]="isUploadingStructure"
                [attr.aria-label]="i18n.t('cmig.removeStructureImage')">
                ×
              </button>
            </div>
            
            <p class="structure-upload-status" *ngIf="isUploadingStructure">{{ i18n.t('cmig.uploading') }}</p>
        </div>
      </div>
    </div>
  </aside>
   
  <!-- Main Content -->
  <main class="main-content">
    <div class="content-area">
      <!-- Results Area (Top) -->
      <div class="results-area">
        <!-- Loading State -->
        <app-loading *ngIf="isGenerating && generatedImages.length === 0" [message]="i18n.t('cmig.generatingImage')"></app-loading>

        <!-- Generated Images Grid -->
        <div class="results-section" *ngIf="generatedImages.length > 0">
          <div class="results-header">
            <h3 class="results-title">{{ i18n.t('cmig.generationResults') }}</h3>
            <p class="results-prompt" *ngIf="generationPrompt">{{ generationPrompt }}</p>
          </div>
          <div class="image-grid">
            <div
              class="image-card"
              *ngFor="let image of generatedImages; let i = index">
              <img [src]="image.url" [alt]="i18n.t('cmig.generatedImageAlt', { index: i + 1 })">
              <button
                type="button"
                class="download-btn"
                (click)="downloadImage(image, $event)"
                [attr.aria-label]="i18n.t('cmig.downloadImage')">
                <svg class="download-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 3V15M12 15L16 11M12 15L8 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div class="results-placeholder" *ngIf="!isGenerating && generatedImages.length === 0">
          <div class="results-placeholder-icon" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
              <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p class="results-placeholder-title">{{ i18n.t('cmig.placeholderTitle') }}</p>
          <ul class="results-placeholder-tips">
            <li>{{ i18n.t('cmig.placeholderTip1') }}</li>
            <li>{{ i18n.t('cmig.placeholderTip2') }}</li>
          </ul>
        </div>
      </div>

      <!-- Prompt Input (Bottom) -->
      <div class="prompt-section">
        <label class="prompt-label" for="custom-model-prompt">{{ i18n.t('cmig.promptLabel') }}</label>
        <textarea
          id="custom-model-prompt"
          class="prompt-input"
          [(ngModel)]="prompt"
          [placeholder]="i18n.t('cmig.promptPlaceholder')"
          rows="1"></textarea>
        <div class="prompt-actions">
          <div class="prompt-actions-note before" [class.hidden]="hasGeneratedImages">

          </div>
          <div class="prompt-actions-note after" [class.hidden]="!hasGeneratedImages">
           
            <p class="important-note"><b>{{ i18n.t('cmig.saveImmediatelyLabel') }}</b> {{ i18n.t('cmig.saveImmediatelyBody') }}</p>
          
          </div>
          <button
            class="generate-btn"
            [disabled]="!selectedModel || !prompt.trim() || isGenerating || isUploadingStructure || (!!structureImagePreview && !structureImageId)"
            (click)="generateImages()">
            <i class="fas fa-magic generate-btn-icon" aria-hidden="true" *ngIf="!isGenerating"></i>
            <span *ngIf="!isGenerating">{{ i18n.t('cmig.generate') }}</span>
            <span *ngIf="isGenerating">{{ i18n.t('cmig.generating') }}</span>
          </button>
        </div>
        <div class="prompt-actions-note terms">
          <h2 class="terms-title">{{ i18n.t('cmig.termsTitle') }}</h2>
          <h3>{{ i18n.t('cmig.permittedUseLabel') }}</h3>
          <p>{{ i18n.t('cmig.permittedUseBody1') }}<br/><br/></p>
          <p>{{ i18n.t('cmig.permittedUseBody2') }}<br/><br/></p>
          <p>{{ i18n.t('cmig.permittedUseBody3') }}<br/><br/></p>
          <p class="subsection-title">{{ i18n.t('cmig.externalSuppliersTitle') }}</p>
          <p>{{ i18n.t('cmig.externalSuppliersBody') }}<br/><br/></p>
          <p class="subsection-title">{{ i18n.t('cmig.panasonicEmployeesTitle') }}</p>
          <p>{{ i18n.t('cmig.panasonicEmployeesBody') }}<br/><br/></p>

          <h3>{{ i18n.t('cmig.aiModelSelectionTitle') }}</h3>
          <ul class="terms-list">
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.aiModelSelectionDo1') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.aiModelSelectionDoNot1') }}</li>
          </ul>

          <h3>{{ i18n.t('cmig.beforeGenTitle') }}</h3>
          <p class="subsection-title">{{ i18n.t('cmig.doNotWord') }}</p>
          <ul class="terms-list">
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.beforeGenDoNot1') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.beforeGenDoNot2') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.beforeGenDoNot3') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.beforeGenDoNot4') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.beforeGenDoNot5') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.beforeGenDoNot6') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.beforeGenDoNot7') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.beforeGenDoNot8') }}</li>
          </ul>
          <p class="subsection-title">{{ i18n.t('cmig.doWord') }}</p>
          <ul class="terms-list">
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.beforeGenDo1') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.beforeGenDo2') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.beforeGenDo3') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.beforeGenDo4') }}</li>
          </ul>

          <h3>{{ i18n.t('cmig.afterGenTitle') }}</h3>
          <p class="subsection-title">{{ i18n.t('cmig.doNotWord') }}</p>
          <ul class="terms-list">
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.afterGenDoNot1') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.afterGenDoNot2') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.afterGenDoNot3') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.afterGenDoNot4') }}</li>
            <li><b>{{ i18n.t('cmig.doNotWord') }}</b> {{ i18n.t('cmig.afterGenDoNot5') }}</li>
          </ul>
          <p class="subsection-title">{{ i18n.t('cmig.doWord') }}</p>
          <ul class="terms-list">
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo1') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo2') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo3') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo4') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo5') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo6') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo7') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo9') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo10') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.afterGenDo11') }}</li>
          </ul>

          <h3>{{ i18n.t('cmig.bestPracticeTitle') }}</h3>
          <p class="subsection-title">{{ i18n.t('cmig.doWord') }}</p>
          <ul class="terms-list">
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.bestPracticeDo1') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.bestPracticeDo2') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.bestPracticeDo3') }}</li>
            <li><b>{{ i18n.t('cmig.doWord') }}</b> {{ i18n.t('cmig.bestPracticeDo4') }}</li>
          </ul>
        </div>

      </div>
    </div>
  </main>
</div>

<app-toast></app-toast>
